import { useCallback, useState } from 'react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;
const ADMIN_HEADERS = ADMIN_KEY ? { headers: { 'X-Admin-Key': ADMIN_KEY } } : {};

/**
 * Available override decisions
 */
export const OVERRIDE_DECISIONS = {
  APPROVE: 'approve', // Explicitly approve/lock the current recommendation
  FORCE_COMPLEMENT: 'force_complement',
  FORCE_ADDON: 'force_addon',
  FORCE_COMPETITOR: 'force_competitor',
  EXCLUDE: 'exclude',
  AUTO: 'auto', // Clears the override
};

/**
 * Decision labels for UI display
 */
export const DECISION_LABELS = {
  [OVERRIDE_DECISIONS.APPROVE]: 'Approved',
  [OVERRIDE_DECISIONS.FORCE_COMPLEMENT]: 'Force Complement',
  [OVERRIDE_DECISIONS.FORCE_ADDON]: 'Force Add-on',
  [OVERRIDE_DECISIONS.FORCE_COMPETITOR]: 'Force Competitor',
  [OVERRIDE_DECISIONS.EXCLUDE]: 'Exclude',
  [OVERRIDE_DECISIONS.AUTO]: 'Clear Override',
};

/**
 * Hook for managing product relation overrides.
 */
export default function useOverrides() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check if admin key is configured
   */
  const hasAdminKey = Boolean(ADMIN_KEY);

  /**
   * Create or update an override for a product pair.
   *
   * @param {string} anchorProductId - The anchor product ID
   * @param {string} candidateProductId - The candidate product ID
   * @param {string} decision - One of OVERRIDE_DECISIONS values
   * @param {Object} options - Additional options
   * @param {number} options.weightAdjust - Weight adjustment (-0.2 to 0.2)
   * @param {string} options.note - Human-readable note
   * @param {string} options.relationType - Explicit relation type override
   */
  const createOverride = useCallback(async (anchorProductId, candidateProductId, decision, options = {}) => {
    if (!hasAdminKey) {
      const err = new Error('Admin key not configured');
      setError(err.message);
      throw err;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        anchor_product_id: anchorProductId,
        candidate_product_id: candidateProductId,
        decision,
      };

      if (options.weightAdjust !== undefined) {
        payload.weight_adjust = Math.max(-0.2, Math.min(0.2, options.weightAdjust));
      }
      if (options.note) {
        payload.note = options.note;
      }
      if (options.relationType) {
        payload.relation_type = options.relationType;
      }
      if (options.createdBy) {
        payload.created_by = options.createdBy;
      }

      const { data } = await axios.post(
        `${API_BASE}/api/admin/relations/overrides`,
        payload,
        ADMIN_HEADERS
      );

      return data;
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to save override';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [hasAdminKey]);

  /**
   * Clear an override (set decision to 'auto').
   *
   * @param {string} anchorProductId - The anchor product ID
   * @param {string} candidateProductId - The candidate product ID
   */
  const clearOverride = useCallback(async (anchorProductId, candidateProductId) => {
    return createOverride(anchorProductId, candidateProductId, OVERRIDE_DECISIONS.AUTO);
  }, [createOverride]);

  /**
   * Fetch all overrides for an anchor product.
   *
   * @param {string} anchorProductId - The anchor product ID
   */
  const getOverrides = useCallback(async (anchorProductId) => {
    if (!hasAdminKey) {
      return [];
    }

    try {
      const params = anchorProductId ? `?anchor_id=${anchorProductId}` : '';
      const { data } = await axios.get(
        `${API_BASE}/api/admin/relations/overrides${params}`,
        ADMIN_HEADERS
      );
      return data?.items || data || [];
    } catch (err) {
      console.error('Failed to fetch overrides:', err);
      return [];
    }
  }, [hasAdminKey]);

  return {
    createOverride,
    clearOverride,
    getOverrides,
    loading,
    error,
    hasAdminKey,
  };
}
